import { useCallback, useMemo } from 'react';
import { Select, Text } from '@mantine/core';
import type { ComboboxParsedItem, OptionsFilter } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  useUploadStore,
  getInFlightUploadCount,
  getProcessingCount,
} from '@/features/upload/uploadStore';
import { bcInt } from '@lib2/model/util/string/BaseConverter';

const collator = new Intl.Collator(undefined, { sensitivity: 'base' });

const optionsFilter: OptionsFilter = ({ options, search }) => {
  const terms = search.toLowerCase().trim().split(' ').filter(Boolean);
  if (terms.length === 0) return options;

  return options.reduce<ComboboxParsedItem[]>((acc, item) => {
    if ('group' in item) {
      const filtered = item.items.filter((opt) => {
        const words = opt.label.toLowerCase().split(' ');
        return terms.every((t) => words.some((w) => w.includes(t)));
      });
      if (filtered.length > 0) {
        acc.push({ group: item.group, items: filtered });
      }
    } else {
      const words = item.label.toLowerCase().split(' ');
      if (terms.every((t) => words.some((w) => w.includes(t)))) {
        acc.push(item);
      }
    }
    return acc;
  }, []);
};

export function CustomerSelector() {
  const { customerInfo, selectedCId, setSelectedCId, canAccessSubAccounts, selectedCustomer } = useAuth();
  const reset = useUploadStore((s) => s.reset);

  const handleChange = useCallback(
    (value: string | null) => {
      if (!value) return;
      const newCId = parseInt(value, 10);
      if (newCId === selectedCId) return;

      // Clears leftover upload items (they belong to the account being left)
      // and, when server-side processing continues unattended, says so —
      // one toast per source account so repeated switches update, not stack.
      const switchTo = () => {
        const processingCount = getProcessingCount();
        if (useUploadStore.getState().items.length > 0) {
          reset();
        }
        if (processingCount > 0 && selectedCId != null) {
          const label = selectedCustomer?.cfg.label ?? 'the previous account';
          const toastId = `upload-processing-continues-${selectedCId}`;
          notifications.hide(toastId);
          notifications.show({
            id: toastId,
            title: 'Processing continues',
            message:
              processingCount === 1
                ? `1 video from ${label} is still processing — it will appear in that account's library when done.`
                : `${processingCount} videos from ${label} are still processing — they will appear in that account's library when done.`,
            color: 'blue',
            autoClose: 6000,
          });
        }
        setSelectedCId(newCId);
      };

      // Only uploads the client is still sending need confirmation; items in
      // server-side processing are unaffected by a switch.
      const inFlightCount = getInFlightUploadCount();
      if (inFlightCount > 0) {
        modals.openConfirmModal({
          title: 'Cancel active uploads?',
          children: (
            <Text size="sm">
              You have {inFlightCount} upload{inFlightCount !== 1 ? 's' : ''} in progress.
              Switching accounts will cancel {inFlightCount !== 1 ? 'them' : 'it'}.
            </Text>
          ),
          labels: { confirm: 'Switch account', cancel: 'Keep uploading' },
          confirmProps: { color: 'red' },
          onConfirm: switchTo,
        });
        return;
      }

      switchTo();
    },
    [selectedCId, setSelectedCId, reset, selectedCustomer]
  );

  const data = useMemo(() => {
    if (!customerInfo) return [];

    const sortedSubs = [...customerInfo.subcustomers].sort((a, b) =>
      collator.compare(a.cfg.label, b.cfg.label)
    );

    return [
      {
        group: 'Parent Account',
        items: [{
          value: String(customerInfo.customer.cfg.cId),
          label: customerInfo.customer.cfg.label,
        }],
      },
      {
        group: `Sub-Accounts (${sortedSubs.length})`,
        items: sortedSubs.map(sub => ({
          value: String(sub.cfg.cId),
          label: sub.cfg.label,
        })),
      },
    ];
  }, [customerInfo]);

  if (!customerInfo || !canAccessSubAccounts) return null;

  return (
    <div>
      <Select
        data={data}
        value={selectedCId != null ? String(selectedCId) : null}
        onChange={handleChange}
        searchable
        filter={optionsFilter}
        size="sm"
        w={{ base: 190, sm: 220, lg: 260 }}
        comboboxProps={{ width: 320, shadow: 'md' }}
        placeholder="Select account"
        maxDropdownHeight={320}
        limit={100}
        nothingFoundMessage="No accounts found"
      />
      {selectedCId != null && (
        <Text size="xs" c="dimmed" mt={2} ff="monospace">
          ID: {bcInt.getStr(selectedCId)}
        </Text>
      )}
    </div>
  );
}
